import { useState } from 'react';
import styled from 'styled-components';
import { Ace } from 'ace-builds';
import AceEditor, { IAceEditorProps } from 'react-ace';
import ace from 'ace-builds/src-noconflict/ace';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/mode-sql';
import 'ace-builds/src-noconflict/mode-yaml';
import 'ace-builds/src-noconflict/mode-graphqlschema';
import { IconButton, Theme, Tooltip } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CodeIcon from '@mui/icons-material/Code';
import { ReactComponent as CopyIcon } from '../../assets/copy.svg';
import { ReactComponent as VIcon } from '../../assets/v.svg';
import { Flex } from '../Flex';
import SvgIcon from '../SvgIcon';
import { WarehouseType } from '../../types';
import { componentToken, fontFamily, declaration } from '../../style/utils';
import { formatSqlCode } from './utils';

type EditorColors = Theme['tokens']['components']['codeEditor']['colors'];

// Configure ace editor base path for proper worker loading
// This prevents "Failed to load worker" errors in development
ace.config.set(
  'basePath',
  'https://cdn.jsdelivr.net/npm/ace-builds@1.32.0/src-noconflict/',
);
ace.config.set(
  'modePath',
  'https://cdn.jsdelivr.net/npm/ace-builds@1.32.0/src-noconflict/',
);
ace.config.set(
  'themePath',
  'https://cdn.jsdelivr.net/npm/ace-builds@1.32.0/src-noconflict/',
);
ace.config.set(
  'workerPath',
  'https://cdn.jsdelivr.net/npm/ace-builds@1.32.0/src-noconflict/',
);

const Buttons = styled(Flex)<{ $grayedOut?: boolean }>`
  position: absolute;
  top: 8px;
  right: 8px;
  background: ${componentToken('codeEditor.toolbar.background')};
  border: ${({ theme, $grayedOut }) =>
    $grayedOut
      ? theme.tokens.components.codeEditor.toolbar.disabledBorder
      : theme.tokens.components.codeEditor.toolbar.border};
  border-radius: ${componentToken('codeEditor.toolbar.radius')};

  .MuiIconButton-root {
    ${({ theme }) =>
      declaration(
        'border-radius',
        theme.tokens.components.codeEditor.toolbar.buttonRadius,
      )}

    &:hover {
      ${({ theme }) =>
        declaration(
          'background',
          theme.tokens.components.codeEditor.toolbar.buttonHoverBackground,
        )}
    }
  }
`;

// A look without an Ace theme draws the editor in its own colors. Grayed out,
// it follows the Exosphere disabled textarea: a lighter border and secondary
// text with no syntax colors. Only an editable editor marks the active line.
const getEditorColorRules = (
  c: EditorColors,
  grayedOut?: boolean,
  readOnly?: boolean,
) => {
  const markActiveLine = !grayedOut && !readOnly;
  return `
  && {
    background: ${c.background};
    color: ${c.text};
    border: 1px solid ${grayedOut ? c.disabledBorder : c.border};
  }

  && .ace_gutter {
    background: ${c.gutter};
    color: ${grayedOut ? c.disabledGutterText : c.gutterText};
    border-right: 1px solid ${grayedOut ? c.disabledBorder : c.border};
  }

  && .ace_gutter-active-line {
    background: ${markActiveLine ? c.activeGutter : 'none'};
    color: ${
      markActiveLine ? c.text : grayedOut ? c.disabledGutterText : c.gutterText
    };
  }

  && .ace_marker-layer .ace_active-line {
    background: ${markActiveLine ? c.activeLine : 'none'};
  }

  && .ace_marker-layer .ace_selection {
    background: ${c.selection};
  }

  && .ace_marker-layer .ace_selected-word {
    background: none;
    border: 1px solid ${c.border};
  }

  && .ace_marker-layer .ace_bracket {
    border-color: ${c.border};
  }

  && .ace_cursor {
    color: ${c.text};
    ${grayedOut ? 'display: none;' : ''}
  }

  && .ace_indent-guide {
    background: none;
    box-shadow: ${c.indentGuide};
  }

  && .ace_line,
  && .ace_line * {
    color: ${grayedOut ? c.secondaryText : c.text};
  }

  ${
    grayedOut
      ? ''
      : `
    && .ace_line .ace_keyword,
    && .ace_line .ace_storage,
    && .ace_line .ace_meta.ace_tag {
      color: ${c.keyword};
      font-weight: ${c.keywordWeight};
    }

    && .ace_line .ace_keyword.ace_operator {
      color: ${c.text};
      font-weight: ${c.operatorWeight};
    }

    && .ace_line .ace_support.ace_function,
    && .ace_line .ace_entity.ace_name.ace_function {
      color: ${c.function};
    }

    && .ace_line .ace_string {
      color: ${c.string};
    }

    && .ace_line .ace_constant {
      color: ${c.number};
    }

    && .ace_line .ace_comment {
      color: ${c.secondaryText};
      font-style: italic;
    }
  `
  }
`;
};

const CopyIconStyled = styled(CopyIcon)`
  path {
    stroke: ${({ theme }) => theme.palette.code.editorIcon};
  }
`;

const formatCode = (
  code: string,
  mode?: string,
  warehouseType?: WarehouseType,
): string => (mode === 'sql' ? formatSqlCode(code, warehouseType) : code);

type Props = Omit<IAceEditorProps, 'theme'> & {
  mode?: string;
  height?: string;
  fontSize?: number;
  grayedOut?: boolean;
  hideLineNumbers?: boolean;
  warehouseType?: WarehouseType;
};

// react-ace can't unset a theme once set, so "no theme" is Ace's built-in
// default rather than undefined.
const ACE_DEFAULT_THEME = 'textmate';

const CodeEditor = styled(AceEditor).attrs(
  ({
    theme,
    fontSize = 13,
    width = '100%',
    showPrintMargin = false,
    grayedOut,
    hideLineNumbers,
    mode = 'text',
    ...props
  }: Props & { theme: Theme }) => ({
    // Workaround to the fact that the AceEditor accepts a theme prop itself
    theme:
      grayedOut || !theme.tokens.components.codeEditor.aceTheme
        ? ACE_DEFAULT_THEME
        : theme.tokens.components.codeEditor.aceTheme,
    colorTheme: theme,
    fontSize,
    width,
    showPrintMargin,
    grayedOut,
    onLoad: (editor: Ace.Editor) => {
      editor.renderer.setScrollMargin(10, 10, 0, 0);
      if (hideLineNumbers) {
        editor.renderer.setShowGutter(false);
        editor.renderer.setPadding(10);
      }
    },
    mode,
    ...props,
  }),
)`
  border-radius: ${({ colorTheme }) =>
    colorTheme.tokens.components.codeEditor.radius};

  * {
    font-family: ${fontFamily('code')};
  }

  .error-marker {
    position: absolute;
    border-bottom: 1px solid
      ${({ colorTheme }) => colorTheme.palette.status.error.main};
    border-radius: 0;
    min-width: 3px;
    background-color: ${({ colorTheme }) =>
      colorTheme.palette.status.error.main};
    opacity: 0.3;
    width: 100% !important;
    left: 0 !important;
  }

  .ace_error {
    background-image: none;
  }

  .ace_gutter-cell.ace_error {
    background-color: ${({ colorTheme }) =>
      colorTheme.palette.status.error.main};
    color: ${({ colorTheme }) => colorTheme.palette.content.inverse};
  }

  ${({ readOnly }) =>
    readOnly &&
    `
      .ace_cursor {
          opacity: 0
      }
      .ace_active-line {
        background: none !important;
      }
      .ace_gutter-active-line {
        background: none !important;
      }
`};

  ${({ grayedOut, readOnly, colorTheme }) =>
    !colorTheme.tokens.components.codeEditor.aceTheme &&
    getEditorColorRules(
      colorTheme.tokens.components.codeEditor.colors,
      grayedOut,
      readOnly,
    )}

  ${({ grayedOut, colorTheme }) =>
    grayedOut &&
    colorTheme.tokens.components.codeEditor.aceTheme &&
    `
      &.ace-tm {
        opacity: 0.5;
        filter: grayscale(100%) brightness(85%);
      }

      .ace_content {
        background-color: ${colorTheme.palette.code.editorBg} !important; 
      }

      .ace_gutter {
        background: ${colorTheme.palette.code.gutterBg} !important; 
        color: ${colorTheme.palette.code.gutterText} !important; 
      }

      .ace_cursor {
        display: none !important; 
      }

      .ace_marker-layer .ace_selection {
        background: ${colorTheme.palette.code.selection} !important; 
      }
`};
`;

const CopyTooltip = ({ isCopied }: { isCopied: boolean }) => (
  <Flex sx={{ alignItems: 'center', gap: 0.5 }}>
    {isCopied && (
      <SvgIcon strokeColor="icon.inverse">
        <VIcon />
      </SvgIcon>
    )}
    {isCopied ? 'Copied!' : 'Copy'}
  </Flex>
);

const CodeEditorWrapper = (props: Props) => {
  const [isCopied, setIsCopied] = useState(false);
  const trimmedValue = props.value?.trim() || '';
  const [isFormatted, setIsFormatted] = useState(true);

  // When height is provided, exclude maxLines and minLines to avoid type conflicts
  const { height, maxLines, minLines, warehouseType, ...editorProps } = props;

  const displayValue =
    isFormatted && props.readOnly
      ? formatCode(trimmedValue, props.mode, warehouseType)
      : props.readOnly
      ? trimmedValue
      : props.value || '';

  const handleCopy = () => {
    navigator.clipboard.writeText(displayValue);
    setIsCopied(true);
  };

  const showFormatButton = props.readOnly && props.mode === 'sql';

  const sizeProps = height
    ? { height }
    : {
        maxLines: maxLines || Infinity,
        minLines: minLines || 1,
      };

  return (
    <Flex
      sx={{
        position: 'relative',
        height: props.height,
        width: props.width || '100%',
      }}
    >
      <CodeEditor {...editorProps} {...sizeProps} value={displayValue} />

      <Buttons $grayedOut={props.grayedOut}>
        {showFormatButton && (
          <Tooltip title={isFormatted ? 'Raw' : 'Beautify'}>
            <IconButton onClick={() => setIsFormatted(!isFormatted)}>
              {isFormatted ? (
                <CodeIcon
                  sx={{
                    fontSize: (theme) => theme.tokens.fontSize.lg,
                    color: 'code.editorIcon',
                  }}
                />
              ) : (
                <AutoFixHighIcon
                  sx={{
                    fontSize: (theme) => theme.tokens.fontSize.lg,
                    color: 'code.editorIcon',
                  }}
                />
              )}
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title={<CopyTooltip isCopied={isCopied} />}>
          <IconButton
            onClick={handleCopy}
            onMouseLeave={() => setIsCopied(false)}
          >
            <SvgIcon width={16}>
              <CopyIconStyled />
            </SvgIcon>
          </IconButton>
        </Tooltip>
      </Buttons>
    </Flex>
  );
};

export { CodeEditor as BaseCodeEditor };

export default CodeEditorWrapper;
